import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type {
  RoleWithPermissions,
  Permission,
  PermissionWithRoleCount,
} from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Query Keys ────────────────────────────────────────────

export const rolesKeys = {
  all: ['roles'] as const,
  lists: () => [...rolesKeys.all, 'list'] as const,
  list: () => [...rolesKeys.lists()] as const,
  details: () => [...rolesKeys.all, 'detail'] as const,
  detail: (id: string) => [...rolesKeys.details(), id] as const,
};

export const permissionsKeys = {
  all: ['permissions'] as const,
  lists: () => [...permissionsKeys.all, 'list'] as const,
  list: () => [...permissionsKeys.lists()] as const,
};

// ─── Roles ─────────────────────────────────────────────────

/** `enabled: false` skips the request, e.g. where the role list is not shown. */
export function useRoles(enabled = true) {
  return useQuery<RoleWithPermissions[]>({
    queryKey: rolesKeys.list(),
    queryFn: async () => {
      const { data } = await api.get('/roles');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
    enabled,
  });
}

export function useRole(id: string) {
  return useQuery<RoleWithPermissions>({
    queryKey: rolesKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get(`/roles/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name: string; description?: string; permissionIds?: string[] }) => {
      const { data } = await api.post('/roles', dto);
      return data as RoleWithPermissions;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: { id: string; name?: string; description?: string }) => {
      const { data } = await api.put(`/roles/${id}`, dto);
      return data as RoleWithPermissions;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/roles/${id}`);
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}

export function useAssignPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, permissionIds }: { roleId: string; permissionIds: string[] }) => {
      const { data } = await api.put(`/roles/${roleId}/permissions`, { permissionIds });
      return data as RoleWithPermissions;
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
      // Current user's permissions live in the Zustand auth store, not Query cache.
      // Refetch /auth/me so changes apply without a page reload.
      void useAuthStore.getState().checkAuth();
    },
  });
}

export function useAssignRoleToUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, userIds }: { roleId: string; userIds: string[] }) => {
      const { data } = await api.put('/roles/assign-users', { roleId, userIds });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
      void useAuthStore.getState().checkAuth();
    },
  });
}

// ─── Users ─────────────────────────────────────────────────

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}

// ─── Permissions ───────────────────────────────────────────

export function useAllPermissions() {
  return useQuery<PermissionWithRoleCount[]>({
    queryKey: permissionsKeys.list(),
    queryFn: async () => {
      const { data } = await api.get('/permissions');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreatePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { code: string; name: string; description?: string; module: string }) => {
      const { data } = await api.post('/permissions', dto);
      return data as Permission;
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });
}

export function useUpdatePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: { id: string; name?: string; description?: string }) => {
      const { data } = await api.put(`/permissions/${id}`, dto);
      return data as Permission;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });
}

export function useDeletePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/permissions/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: permissionsKeys.all });
      // The delete took the permission off every role; a cached role would
      // send its dead id back on the next matrix save. [HW-1062]
      qc.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
}
